import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  List,
  ListItemButton,
  ListItemText,
  Paper,
} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import {
  MENTION_KIND_AI,
  MENTION_KIND_GROUP,
} from '../../lib/ticketMentions';

function iconForMentionKind(kind) {
  if (kind === MENTION_KIND_AI) {
    return <SmartToyOutlinedIcon fontSize="small" sx={{ mr: 1.5, opacity: 0.85 }} />;
  }
  if (kind === MENTION_KIND_GROUP) {
    return <GroupsOutlinedIcon fontSize="small" sx={{ mr: 1.5, opacity: 0.85 }} />;
  }
  return <PersonOutlineIcon fontSize="small" sx={{ mr: 1.5, opacity: 0.85 }} />;
}

function secondaryForItem(item) {
  if (item.mentionKind === MENTION_KIND_GROUP) return 'User group';
  if (item.mentionKind === MENTION_KIND_AI) return 'AI worker';
  if (item.mentionKind === 'ai_user') return 'AI user account';
  return 'Team member';
}

/**
 * TipTap mention suggestion list.
 */
const TicketMentionList = forwardRef(function TicketMentionList(props, ref) {
  const { items = [], command } = props;
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  const selectItem = (index) => {
    const item = items[index];
    if (!item || !command) return;
    command({
      id: item.id,
      label: item.label,
    });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (!items.length) return false;
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % items.length);
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (!items.length) {
    return (
      <Paper elevation={8} sx={{ p: 1.5, minWidth: 260 }}>
        <ListItemText
          primary="No matches"
          secondary="Try another name"
          primaryTypographyProps={{ variant: 'body2' }}
          secondaryTypographyProps={{ variant: 'caption' }}
        />
      </Paper>
    );
  }

  return (
    <Paper elevation={8} sx={{ maxHeight: 280, overflow: 'auto', minWidth: 280 }}>
      <List dense disablePadding>
        {items.map((item, index) => (
          <ListItemButton
            key={item.id}
            selected={index === selectedIndex}
            onMouseEnter={() => setSelectedIndex(index)}
            onMouseDown={(event) => {
              event.preventDefault();
              selectItem(index);
            }}
          >
            {iconForMentionKind(item.mentionKind)}
            <ListItemText
              primary={item.label}
              secondary={secondaryForItem(item)}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );
});

export default TicketMentionList;
